import i18next, { type i18n as I18nInstance, type Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { Locale } from '@saniplus/contracts';

export const SUPPORTED_LOCALES: readonly Locale[] = ['fr', 'en'];
/** D-13: French first. `fr-CA` is the product language; `fr` is the API code. */
export const DEFAULT_LOCALE: Locale = 'fr';

export interface CreateI18nOptions {
  /** `{ fr: { common: {...} }, en: { common: {...} } }` — one namespace per module. */
  readonly resources: Resource;
  readonly defaultLocale?: Locale;
  /** Namespaces present in `resources`; defaults to `['common']`. */
  readonly namespaces?: string[];
}

/**
 * One isolated i18next instance per app.
 *
 * `createInstance()` rather than the global: two apps in one test process
 * (and Vitest module reuse) must not share a language.
 */
export function createI18n(options: CreateI18nOptions): I18nInstance {
  const instance = i18next.createInstance();
  void instance.use(initReactI18next).init({
    resources: options.resources,
    lng: options.defaultLocale ?? DEFAULT_LOCALE,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    ns: options.namespaces ?? ['common'],
    defaultNS: 'common',
    // React already escapes.
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  return instance;
}

/** Narrow an arbitrary string (a `?lang=`, a browser language) to the enum. */
export function asLocale(value: string | null | undefined): Locale | null {
  if (value === null || value === undefined) return null;
  const head = value.toLowerCase().split('-')[0];
  return head === 'fr' || head === 'en' ? head : null;
}
